import type { Message } from "@nexus/types"
import { createAsyncThunk } from "@reduxjs/toolkit"

import {
  type EmbedRequestFailure,
  getEmbeddedChatMessages,
  isConversationClosed,
  toEmbedRequestFailure
} from "@/common/services/chat"

import type { EmbedRootState } from "../embedSelectors"
import { reopenClosedEmbedChat } from "./reopenClosedEmbedChat"

interface LoadMoreEmbedMessagesParams {
  chatId: string
  deploymentId: string
  sessionId: string
  agentId: string
}

interface LoadMoreEmbedMessagesResult {
  chatId: string
  messages: Message[]
  hasMore: boolean
}

/**
 * Load more historical messages for infinite scroll
 * Fetches messages older than the oldest currently loaded message
 *
 * A history read refused with `CONVERSATION_CLOSED` means the conversation was
 * deleted under the open tab: it re-initialises the tab into a fresh conversation
 * (`reopenClosedEmbedChat`) and resends nothing. Every other failure rejects as
 * before, with no re-initialisation.
 */
export const loadMoreEmbedMessages = createAsyncThunk<
  LoadMoreEmbedMessagesResult,
  LoadMoreEmbedMessagesParams,
  { state: EmbedRootState; rejectValue: EmbedRequestFailure }
>(
  "embedChat/loadMoreMessages",
  async ({ chatId, deploymentId, sessionId, agentId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const chat = getState().embedChat.chats[chatId]

      if (!chat) {
        throw new Error("Chat not found")
      }

      if (!chat.hasMoreHistory) {
        return { chatId, messages: [], hasMore: false }
      }

      // Get the oldest message ID as cursor
      const oldestMessage = chat.messages[0]
      if (!oldestMessage) {
        return { chatId, messages: [], hasMore: false }
      }

      const response = await getEmbeddedChatMessages({
        chatRoomId: chatId,
        agentId,
        sessionId,
        deploymentId,
        limit: "20",
        sortBy: "createdAt",
        order: "desc", // Get older messages (before cursor)
        before: oldestMessage.id
      })

      // debt: history rows arrive `unknown[]` and are read as `Message` unvalidated —
      //       no message-row Zod schema exists in @nexus/types.
      //       Upgrade trigger: a `MessageSchema` landing there; parse element-wise then.

      // Reverse to maintain chronological order (oldest to newest)
      const messages = (response.messages as Message[]).reverse()

      return {
        chatId,
        messages,
        hasMore: response.hasMore
      }
    } catch (error: unknown) {
      if (isConversationClosed(error)) {
        await dispatch(reopenClosedEmbedChat({ closedChatId: chatId }))
      }
      return rejectWithValue(toEmbedRequestFailure(error, "Failed to load more messages"))
    }
  }
)
